import {Pipe, PipeTransform} from '@angular/core';
import {
    Forecast,
    FORECAST_METADATA,
    Planning,
    PLANNING_METADATA,
    QuantityType,
    Severity,
    STATUSES
} from 'smag-commons/dto/msg';
import {
    ECEntitySubtype,
    ECJuridicalType,
    ECLegalForm,
    ECMemberAccessType,
    ECMemberParticipationType,
    ECMemberState,
    ECMode,
    ECRepresentativeType,
    ECSubjectType,
    EntitySubType,
    EntityType,
    METER_MODEL_CHAIN2GATE_PLUG,
    METER_MODEL_DINOPLUS,
    METER_MODEL_DUEL,
    METER_MODEL_MINI_GATEWAY,
    METER_MODEL_URBAN_DATASET_METER,
    UserType
} from 'smag-commons/cfg/hierarchy';
import {isDefined} from '../commons/commons';
import {entityName} from './entities';
import {I18NService} from '../i18n/i18n.service';
import {PlanningStatus} from 'smag-commons/dto/planning';
import {EC_DOCUMENT_CF, EC_DOCUMENT_CI} from 'smag-commons/cfg/hierarchy/ec';
import {ModelType} from 'smag-commons/cfg/prediction';
import {environment} from '../../environments/environment';
import {
    ALGORITHM_CONDO_OLD,
    ALGORITHM_FANO,
    ALGORITHM_MIXED,
    ALGORITHM_NO_ALGORITHM,
    ALGORITHM_NO_ALGORITHM_OLD_CONDO,
    ECCacvRedistributionMode,
    ECEconomicsPerformance,
    ECMonthStatus,
    STRATEGY_NEW_RULES,
    STRATEGY_NO_STRATEGY,
    STRATEGY_OLD_CONDO
} from 'smag-commons/dto/eco/community';
import {ALGORITHM_CAPPED_VAI, ALGORITHM_THOUSANDTH, ALGORITHM_VAI,} from 'smag-commons/dto/eco';
import {RateMode} from 'smag-commons/cfg';
import {DEFAULT_DECIMAL_SEPARATOR} from '../i18n/i18n-defaults';
import {EC_DOCUMENT_TYPE_OTHER} from '../views/ec-members/new-member/new-ec-member.component';
import {MeasureAggregationDataSource} from 'smag-commons/dto/api';

@Pipe({name: 'nicePath'})
export class EntityNicePathPipe implements PipeTransform {

    constructor() {
    }

    transform(path: string, space: boolean = true): string {
        return path ? path.replace(/\//g, space ? ' > ' : '>') : '';
    }
}

@Pipe({
    name: 'niceValue'
})
export class NiceValuePipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    private static EPSILON = 1e-10;

    private static fixMultiple(val: number, uom: string): [number, string] {
        if (!uom) {
            return [val, uom];
        }
        const aval = Math.abs(val);
        const fixedUom = uom.substring(1) ? uom.substring(1) : uom;
        if (aval >= 1000000000000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000000000000, `T${fixedUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000000000000, `P${fixedUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000000000000, `E${fixedUom}`];
            }
        }
        if (aval >= 1000000000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000000000, `G${fixedUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000000000, `T${fixedUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000000000, `P${fixedUom}`];
            }
        }
        if (aval >= 1000000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000000, `M${fixedUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000000, `G${fixedUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000000, `T${fixedUom}`];
            }
        }
        if (aval >= 1000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000, `k${fixedUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000, `M${fixedUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000, `G${fixedUom}`];
            } else if (uom === 'GW' || uom === 'GWh' || uom === 'GVAR' || uom === 'GVARh') {
                return [val / 1000, `T${fixedUom}`];
            } else if (uom === 'V') {
                return [val / 1000, 'kV'];
            }
        }
        return [val, uom];
    }

    transform(value: number, uom?: string, decimals?: number, showSign?: boolean): string {
        let val: string;
        let u = uom;
        if (!isDefined(value)) {
            return '';
        }
        if (Math.abs(value) < NiceValuePipe.EPSILON) {
            value = 0;
        }
        if (isDefined(decimals)) {
            val = value.toFixed(decimals);
        } else {
            const [v, uu] = NiceValuePipe.fixMultiple(value, uom);
            u = uu;
            const aval = Math.abs(v);
            if (aval % 1 === 0) { // integer, no mantissa
                val = v.toFixed(0);
            } else {
                const decimalPlaces = aval.toString().split('.')[1]?.length || 0;
                if (decimalPlaces === 1 || decimalPlaces === 2) {
                    val = v.toFixed(decimalPlaces);
                } else {
                    val = v.toFixed(2);
                }
            }
        }
        if (parseFloat(val) === 0) {
            val = '0';
        }
        if (u) {
            const space = u === '%' ? '' : ' ';
            if (u === '€') {
                const absVal = Math.abs(value).toFixed(2);
                val = showSign && +val !== 0 ? (+val > 0 ? '+ ' : '- ') + u + space + absVal : u + space + absVal;
            } else {
                val = val + space + u;
            }
        }
        return val.replace('.', this.i18n.decimalSeparator(DEFAULT_DECIMAL_SEPARATOR));
    }
}

@Pipe({name: 'genericPipe'})
export class GenericPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(value: string): string {
        return this.i18n.translate((value.charAt(0).toUpperCase() + value.slice(1)).replace(/_/g, ' '));
    }
}

@Pipe({name: 'entityType'})
export class EntityTypePipe implements PipeTransform {

    constructor() {
    }

    transform(ent: { type: EntityType; sub_type?: EntitySubType }): any {
        return entityName(ent) || '';
    }
}

@Pipe({name: 'quantityName'})
export class QuantityNamePipe implements PipeTransform {

    constructor() {
    }

    transform(qt: QuantityType, fallback?: string): string {
        if (!qt) {
            return fallback || '';
        }
        switch (qt) {
            case QuantityType.ACTIVE_POWER:
                return 'Active power';
            case QuantityType.REACTIVE_POWER:
                return 'Reactive power';
            case QuantityType.THERMAL_POWER:
                return 'Thermal power';
            case QuantityType.INPUT_THERMAL_POWER:
                return 'Input thermal power';
            case QuantityType.STATE_OF_CHARGE:
                return 'State of charge';
            case QuantityType.VOLTAGE:
                return 'Voltage';
            case QuantityType.PHASE_ANGLE:
                return 'Phase angle';
            case QuantityType.INVERSE_ACTIVE_POWER:
                return 'Inverse active power';
            case QuantityType.INVERSE_REACTIVE_POWER:
                return 'Inverse reactive power';
            case QuantityType.CURTAILED_POWER:
                return 'Curtailed power';
            case QuantityType.SHARED_POWER:
                return 'Shared power';
            case QuantityType.GENERATED_POWER:
                return 'Generated power';
            case QuantityType.CONSUMED_POWER:
                return 'Consumed power';
            case QuantityType.INJECTED_POWER:
                return 'Injected power';
            case QuantityType.ABSORBED_POWER:
                return 'Absorbed power';
            case QuantityType.STORAGE_POWER:
                return 'Storage power';
            default:
                return fallback || '';
        }
    }
}

@Pipe({name: 'entityIcon'})
export class EntityIconPipe implements PipeTransform {
    private static readonly ICON_MAP: { [type in EntityType]?: { [subType in EntitySubType | string]?: string } } = environment.icons;

    constructor() {
    }

    transform(entity: { type: EntityType; sub_type?: EntitySubType }): string {
        const stMap = EntityIconPipe.ICON_MAP[entity.type];
        if (!stMap) {
            return null;
        }
        return entity.sub_type ? (stMap[entity.sub_type] || stMap.undetermined) : stMap.undetermined;
    }
}

@Pipe({name: 'notStatus'})
export class NotificationStatusPipe implements PipeTransform {

    constructor() {
    }

    transform(status: string): string {
        switch (status) {
            case STATUSES.STARTED:
                return 'Started';
            case STATUSES.COMMAND_REJECTED:
                return 'Command rejected';
            case STATUSES.COMMAND_RECEIVED:
                return 'Command received';
            case STATUSES.COMMAND_STARTED:
                return 'Command started';
            case STATUSES.COMMAND_CANCELED:
                return 'Command canceled';
            case STATUSES.COMMAND_EXPIRED:
                return 'Command expired';
            case STATUSES.COMMAND_OK:
                return 'Command Ok';
            case STATUSES.COMMAND_KO:
                return 'Command Ko';
            case STATUSES.PLANNING_OK:
                return 'Planning Ok';
            case STATUSES.PLANNING_KO:
                return 'Planning Ko';
            case STATUSES.PLANNING:
                return 'Planning';
            case STATUSES.EC_CONSUMPTION_ADVICE:
                return 'Consumption advice available';
            case STATUSES.EC_WEEKLY_PERFORMANCE:
                return 'Weekly performance available';
            case STATUSES.EC_STATISTICS_FAIL:
                return 'Member counter calculation failed';
            case STATUSES.EC_MISSING_CONSUMPTION_ADVICE:
                return 'Missing consumption advice for members';
            case STATUSES.EC_WEEKLY_PERFORMANCE_FAIL:
                return 'Weekly performance computation failure for members';
            case STATUSES.EC_CONSUMPTION_ADVICE_SEND_FAIL:
                return 'Consumption advice sending failure';
            case STATUSES.EC_WEEKLY_PERFORMANCE_SEND_FAIL:
                return 'Weekly performance sending failure';
            default:
                return status;
        }
    }
}

@Pipe({name: 'notSeverity'})
export class NotificationSeverityPipe implements PipeTransform {

    constructor() {
    }

    transform(severity: Severity): string {
        switch (severity) {
            case Severity.NORMAL:
                return 'Normal';
            case Severity.INFO:
                return 'Information';
            case Severity.ALERT:
                return 'Alert';
            default:
                return severity as string;
        }
    }
}

@Pipe({name: 'notSeverityIcon'})
export class NotificationSeverityIconPipe implements PipeTransform {

    constructor() {
    }

    transform(severity: Severity): string {
        switch (severity) {
            case Severity.NORMAL:
                return 'fal-check-circle';
            case Severity.INFO:
                return 'fas-info-circle';
            case Severity.ALERT:
                return 'fal-exclamation-circle';
            default:
                return 'fal-question-circle';
        }
    }
}

@Pipe({name: 'planningStatus'})
export class PlanningStatusPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(status: PlanningStatus): string {
        switch (status) {
            case PlanningStatus.OK:
                return this.i18n.translate('Fine');
            case PlanningStatus.IMPOSSIBLE:
                return this.i18n.translate('Unfeasible');
            case PlanningStatus.FAIL:
                return this.i18n.translate('Failed');
            default:
                return status;
        }
    }
}

@Pipe({name: 'planningStatusIcon'})
export class PlanningStatusIconPipe implements PipeTransform {

    constructor() {
    }

    transform(status: PlanningStatus): string {
        switch (status) {
            case PlanningStatus.OK:
                return 'fal-check-circle';
            case PlanningStatus.IMPOSSIBLE:
                return 'fas-exclamation-triangle';
            case PlanningStatus.FAIL:
                return 'fal-times-circle';
            default:
                return null;
        }
    }
}

@Pipe({name: 'planningSource'})
export class PlanningSourcePipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(pl: Planning): string {
        if (!pl.metadata) {
            return null;
        }
        switch (pl.metadata[PLANNING_METADATA.SOURCE_KEY]) {
            case PLANNING_METADATA.SOURCE_SCHEDULED:
                return this.i18n.translate('Scheduled');
            case PLANNING_METADATA.SOURCE_CONSTRAINT:
                return this.i18n.translate('Constraint');
            case PLANNING_METADATA.SOURCE_COMMAND:
                return this.i18n.translate('Command');
            case PLANNING_METADATA.SOURCE_DEVIATION:
                return this.i18n.translate('Deviation');
            case PLANNING_METADATA.SOURCE_FAILURE:
                return this.i18n.translate('Failure');
            default:
                return pl.metadata[PLANNING_METADATA.SOURCE_KEY];
        }
    }
}

@Pipe({name: 'planningStrategy'})
export class PlanningStrategyPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(pl: Planning): string {
        if (!pl.metadata) {
            return null;
        }
        switch (pl.metadata[PLANNING_METADATA.STRATEGY_KEY]) {
            case PLANNING_METADATA.STRATEGY_NEXT24H:
                return this.i18n.translate('Next 24h');
            case PLANNING_METADATA.STRATEGY_TOMORROW:
                return this.i18n.translate('Tomorrow');
            case PLANNING_METADATA.STRATEGY_DAY_AHEAD:
                return this.i18n.translate('Day ahead');
            default:
                return pl.metadata[PLANNING_METADATA.STRATEGY_KEY];
        }
    }
}

@Pipe({name: 'forecastStrategy'})
export class ForecastStrategyPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(fc: Forecast): string {
        if (!fc.metadata) {
            return null;
        }
        switch (fc.metadata[FORECAST_METADATA.STRATEGY_KEY]) {
            case FORECAST_METADATA.STRATEGY_NEXT24H:
                return this.i18n.translate('Next 24h');
            case FORECAST_METADATA.STRATEGY_TOMORROW:
                return this.i18n.translate('Tomorrow');
            case FORECAST_METADATA.STRATEGY_DAY_AHEAD:
                return this.i18n.translate('Day ahead');
            default:
                return fc.metadata[FORECAST_METADATA.STRATEGY_KEY];
        }
    }
}

@Pipe({name: 'ecMemberJuridicalType'})
export class ECMemberJuridicalTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECJuridicalType): string {
        switch (type) {
            case ECJuridicalType.CORPORATE_ENTITY:
                return 'Corporate entity';
            case ECJuridicalType.INDIVIDUAL:
                return 'Individual';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberLegalFormType'})
export class ECMemberLegalFormTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECLegalForm): string {
        switch (type) {
            case ECLegalForm.OTHER_INDIVIDUALS:
                return 'Other individuals';
            case ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE:
                return 'Entrepreneur, sole proprietorship, agricultural enterprise';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberSubjectType'})
export class ECMemberSubjectTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECSubjectType): string {
        switch (type) {
            case ECSubjectType.PMI:
                return 'SME';
            case ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY:
                return 'Territorial body, local authority, other body';
            case ECSubjectType.OTHER:
                return 'Other';
            case ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW:
                return 'Association with legal personality under private law';
            case ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS:
                return 'Associated firm company of professionals';
            case ECSubjectType.COMMUNITY:
                return 'Community';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberEntitySubtype'})
export class ECMemberEntitySubtypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECEntitySubtype): string {
        switch (type) {
            case ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION:
                return 'Environment prevention organization';
            case ECEntitySubtype.STATE:
                return 'State';
            case ECEntitySubtype.REGION:
                return 'Region';
            case ECEntitySubtype.ISLAND_COMMUNITY:
                return 'Island community';
            case ECEntitySubtype.PROVINCE:
                return 'Province';
            case ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION:
                return 'Local administration organization';
            case ECEntitySubtype.MOUNTAIN_COMMUNITY:
                return 'Mountain community';
            case ECEntitySubtype.METROPOLITAN_CITY:
                return 'Metropolitan city';
            case ECEntitySubtype.RELIGIOUS_ORGANIZATION:
                return 'Religious organization';
            case ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION:
                return 'Research and training organization';
            case ECEntitySubtype.MUNICIPALITY:
                return 'Municipality';
            case ECEntitySubtype.MUNICIPALITIES_UNION:
                return 'Municipalities union';
            case ECEntitySubtype.THIRD_SECTOR_ORGANIZATION:
                return 'Third sector organization';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecRepresentativeType'})
export class ECRepresentativeTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECRepresentativeType): string {
        switch (type) {
            case ECRepresentativeType.NATURAL_PERSON:
                return 'Natural person';
            case ECRepresentativeType.PRIVATE_INSTITUTION:
                return 'Private institution';
            case ECRepresentativeType.PUBLIC_INSTITUTION:
                return 'Public institution';
            default:
                return type;
        }
    }
}


@Pipe({name: 'ecMemberState'})
export class ECMemberStatePipe implements PipeTransform {

    constructor() {
    }

    transform(state: ECMemberState): string {
        switch (state) {
            case ECMemberState.NOT_INVITED:
                return 'Not invited';
            case ECMemberState.PENDING:
                return 'Pending';
            case ECMemberState.REGISTERED:
                return 'Registered';
            case ECMemberState.CANCELED:
                return 'Canceled';
            default:
                return state;
        }
    }
}

@Pipe({name: 'ecMemberStateIcon'})
export class ECMemberStateIconPipe implements PipeTransform {

    constructor() {
    }

    transform(state: ECMemberState): string {
        switch (state) {
            case ECMemberState.NOT_INVITED:
                return 'fal-clock';
            case ECMemberState.PENDING:
                return 'fal-clock';
            case ECMemberState.REGISTERED:
                return 'fal-check-circle';
            case ECMemberState.CANCELED:
                return 'fal-trash-alt';
            default:
                return 'fal-question-circle';
        }
    }
}

@Pipe({name: 'ecSubjectParticipationTypeIcon'})
export class ECSubjectParticipationTypeIconPipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECMemberParticipationType): string {
        switch (type) {
            case ECMemberParticipationType.MEMBER:
                return 'fal-home';
            case ECMemberParticipationType.THIRD_PARTY_PRODUCER:
                return 'fal-puzzle-piece';
            default:
                return 'fal-question-circle';
        }
    }
}

@Pipe({name: 'ecSubjectAccessTypeIcon'})
export class ECSubjectAccessTypeIconPipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECMemberAccessType): string {
        switch (type) {
            case ECMemberAccessType.MOBILE:
                return 'fal-mobile-android';
            case ECMemberAccessType.ADVANCED:
                return 'fal-crown';
            case ECMemberAccessType.NONE:
                return 'fal-ban';
            default:
                return 'fal-question-circle';
        }
    }
}

@Pipe({name: 'ecDocumentType'})
export class ECDocumentTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: string): string {
        switch (type) {
            case EC_DOCUMENT_CI:
                return 'Identity card';
            case EC_DOCUMENT_CF:
                return 'Fiscal code';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecDocumentTypeI18n'})
export class ECDocumentTypeI18nPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(type: string): string {
        switch (type) {
            case EC_DOCUMENT_CI:
                return this.i18n.translate('Identity document');
            case EC_DOCUMENT_CF:
                return this.i18n.translate('Fiscal code');
            case EC_DOCUMENT_TYPE_OTHER:
                return this.i18n.translate(EC_DOCUMENT_TYPE_OTHER);
            default:
                return type;
        }
    }
}

@Pipe({name: 'predictiveModelType'})
export class PredictiveModelTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ModelType): string {
        switch (type) {
            case ModelType.PV:
                return 'Pv - Production model for photovoltaic systems';
            case ModelType.WIND:
                return 'Wind - Production model for wind systems';
            case ModelType.LOAD:
                return 'Load - Electric load models';
            case ModelType.THERMAL_LOAD:
                return 'Thermal load - Thermal load models';
            case ModelType.PROSUMER:
                return 'Prosumer - Public network exchange model';
            default:
                return type;
        }
    }
}

@Pipe({name: 'predictiveModelIcon'})
export class PredictiveModelIconPipe implements PipeTransform {

    constructor() {
    }

    transform(type: ModelType): string {
        switch (type) {
            case ModelType.PV:
                return 'fal-solar-panel';
            case ModelType.WIND:
                return 'fal-wind';
            case ModelType.LOAD:
                return 'fal-plug';
            case ModelType.THERMAL_LOAD:
                return 'fal-burn';
            case ModelType.PROSUMER:
                return 'fal-atom';
            default:
                return null;
        }
    }
}

@Pipe({name: 'userType'})
export class UserTypePipe implements PipeTransform {
    constructor() {
    }

    transform(type: UserType): string {
        switch (type) {
            case UserType.HOUSEHOLD:
                return 'Household';
            case UserType.PUBLIC_LIGHTING:
                return 'Public lighting consumers';
            case UserType.PUBLIC_CHARGER:
                return 'Public charger consumers';
            case UserType.GENERIC:
                return 'Other low voltage consumer';
            default:
                return '';
        }
    }
}

@Pipe({name: 'rateMode'})
export class RateModePipe implements PipeTransform {
    constructor() {
    }

    transform(type: RateMode): string {
        switch (type) {
            case RateMode.SINGLE:
                return 'single hourly';
            case RateMode.HOURLY:
                return 'time slots F1, F2 and F3';
            default:
                return '';
        }
    }
}

@Pipe({name: 'radioRateMode'})
export class RadioRateModePipe implements PipeTransform {
    constructor() {
    }

    transform(type: RateMode): string {
        switch (type) {
            case RateMode.SINGLE:
                return 'Single hourly';
            case RateMode.HOURLY:
                return 'Time slots';
            default:
                return '';
        }
    }
}

@Pipe({name: 'radioCacvMode'})
export class RadioCacvModePipe implements PipeTransform {
    constructor() {
    }

    transform(type: ECCacvRedistributionMode): string {
        switch (type) {
            case ECCacvRedistributionMode.PLANTS:
                return 'Plants (of members or third part producers)';
            case ECCacvRedistributionMode.REDISTRIBUTION:
                return 'Consumer utilities';
            case ECCacvRedistributionMode.SET_ASIDE:
                return 'Set aside';
            case ECCacvRedistributionMode.AS_TIP:
                return 'Split between plants and consumer utilities in the same way as TIP';
            case ECCacvRedistributionMode.UNDEFINED:
                return 'Undefined__{f}';
            default:
                return '';
        }
    }
}

@Pipe({name: 'cacvMode'})
export class CacvModePipe implements PipeTransform {
    constructor() {
    }

    transform(type: ECCacvRedistributionMode): string {
        switch (type) {
            case ECCacvRedistributionMode.PLANTS:
                return 'Plants redistribution';
            case ECCacvRedistributionMode.REDISTRIBUTION:
                return 'Consumer utilities redistribution';
            case ECCacvRedistributionMode.SET_ASIDE:
                return 'Excluded from the redistribution';
            case ECCacvRedistributionMode.AS_TIP:
                return 'As TIP';
            case ECCacvRedistributionMode.UNDEFINED:
                return 'Undefined__{f}';
            default:
                return '';
        }
    }
}

@Pipe({name: 'strategy'})
export class StrategyPipe implements PipeTransform {

    constructor() {
    }

    transform(type: string): string {
        switch (type) {
            case STRATEGY_NO_STRATEGY:
                return 'No strategy';
            case STRATEGY_OLD_CONDO:
                return 'Condominium transition regulations';
            case STRATEGY_NEW_RULES:
                return 'Default';
            case '':
                return 'Strategies not available';
            default:
                return type;
        }
    }
}

@Pipe({name: 'redistributedAlgorithm'})
export class RedistributedAlgorithmPipe implements PipeTransform {

    constructor() {
    }

    transform(type: string): string {
        switch (type) {
            case ALGORITHM_NO_ALGORITHM:
                return 'No algorithm';
            case ALGORITHM_NO_ALGORITHM_OLD_CONDO:
                return 'No algorithm';
            case ALGORITHM_THOUSANDTH:
                return 'Thousandths';
            case ALGORITHM_VAI:
                return 'Per capita sharing';
            case ALGORITHM_CAPPED_VAI:
                return 'Per capita sharing with CAP';
            case ALGORITHM_MIXED:
                return 'Mixed';
            case ALGORITHM_FANO:
                return 'Founding members and fund with daytime hours';
            case ALGORITHM_CONDO_OLD:
                return 'Condominium transition regulations';
            case '':
                return 'Please select a redistribution algorithm';
            default:
                return type;
        }
    }
}

@Pipe({name: 'performanceIcon'})
export class PerformanceIconPipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECEconomicsPerformance): string {
        switch (type) {
            case ECEconomicsPerformance.GOOD:
                return 'fal-smile';
            case ECEconomicsPerformance.MEDIUM:
                return 'fal-meh';
            case ECEconomicsPerformance.BAD:
                return 'fal-frown';
            default:
                return null;
        }
    }
}

@Pipe({name: 'ecMode'})
export class ECModePipe implements PipeTransform {

    constructor() {
    }

    transform(mode: ECMode): string {
        switch (mode) {
            case ECMode.BASE:
                return 'Base';
            case ECMode.ADVANCED:
                return 'Advanced';
            case ECMode.HYBRID:
                return 'Hybrid';
            default:
                return mode;
        }
    }
}

@Pipe({name: 'meterModel'})
export class MeterModelPipe implements PipeTransform {

    constructor() {
    }

    transform(model: string): string {
        switch (model) {
            case METER_MODEL_CHAIN2GATE_PLUG:
                return 'Chain2Gate Plug';
            case METER_MODEL_DINOPLUS:
                return 'Dino+';
            case METER_MODEL_DUEL:
                return 'DUEL';
            case METER_MODEL_MINI_GATEWAY:
                return 'MiniGateway NB-IoT';
            case METER_MODEL_URBAN_DATASET_METER:
                return 'Urban Dataset generic meter';
            default:
                return model;
        }
    }
}

@Pipe({name: 'ecMonthStatus'})
export class ECMonthStatusPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(status: ECMonthStatus): string {
        switch (status) {
            case ECMonthStatus.GSE_UPLOADED:
                return this.i18n.translate('GSE uploaded');
            case ECMonthStatus.GSE_NOT_UPLOADED:
                return this.i18n.translate('GSE not uploaded');
            case ECMonthStatus.ONGOING:
                return this.i18n.translate('Ongoing');
            default:
                return status;
        }
    }
}


@Pipe({name: 'dataSource'})
export class DataSourcePipe implements PipeTransform {
    constructor() {
    }

    transform(dataSource: string): string {
        switch (dataSource) {
            case MeasureAggregationDataSource.GSE:
                return 'GSE';
            case MeasureAggregationDataSource.METER:
                return 'Smart meter';
            case MeasureAggregationDataSource.FILE:
                return 'File Excel';
            case MeasureAggregationDataSource.PROFILE:
                return 'Statistical profile';
            case MeasureAggregationDataSource.CASCADE:
                return 'Cascade';
            default:
                return dataSource;
        }
    }
}

@Pipe({name: 'filteringTypesTranslation'})
export class FilteringTypesTranslationPipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECSubjectType | ECLegalForm): string {
        switch (type) {
            case ECSubjectType.OTHER:
                return 'Other legal person';
            case ECSubjectType.PMI:
                return 'SME';
            case ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY:
                return 'Territorial body, local authority, other body';
            case ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW:
                return 'Association with legal personality under private law';
            case ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS:
                return 'Associated firm company of professionals';
            case ECLegalForm.OTHER_INDIVIDUALS:
                return 'Other individuals';
            case ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE:
                return 'Entrepreneur, sole proprietorship, agricultural enterprise';
            default:
                return type;
        }
    }
}
